import React from 'react'
import moment from 'moment/moment'

// ── SVG Icons ─────────────────────────────────────────────────────────────────

const IcoWarning = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <path d="M12 3 L2.5 20 H21.5 Z" stroke="white" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
    <line x1="12" y1="10" x2="12" y2="15" stroke="white" strokeWidth="2.2" strokeLinecap="round"/>
    <circle cx="12" cy="18.2" r="1.1" fill="white"/>
  </svg>
)

const IcoDisconnect = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <path d="M9.5 14.5a5 5 0 0 0 6.36.36l3-3a5 5 0 0 0-7.07-7.07L10 6.5" stroke="white" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M14.5 9.5a5 5 0 0 0-6.36-.36l-3 3a5 5 0 0 0 7.07 7.07L14 17.5" stroke="white" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="4.5" y1="4.5" x2="19.5" y2="19.5" stroke="white" strokeWidth="1.9" strokeLinecap="round"/>
  </svg>
)

const IcoClock = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="2"/>
    <polyline points="12 7 12 12.5 15.5 14.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const IcoTruck = () => (
  <svg width="20" height="14" viewBox="0 0 32 22" fill="none" stroke="#595959" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {/* Trailer */}
    <rect x="1" y="3" width="18" height="12" rx="1.5"/>
    {/* Cab */}
    <path d="M19 7 H25 L29 12 V17 H19 Z"/>
    {/* Cab window */}
    <path d="M20.5 8.5 H24 L26.5 12 H20.5 Z" fill="#595959" fillOpacity="0.18" stroke="none"/>
    {/* Wheels */}
    <circle cx="7"  cy="18" r="2.8" fill="#595959" stroke="none"/>
    <circle cx="15" cy="18" r="2.8" fill="#595959" stroke="none"/>
    <circle cx="25" cy="18" r="2.8" fill="#595959" stroke="none"/>
    {/* Chassis line */}
    <line x1="1" y1="15" x2="19" y2="15"/>
  </svg>
)

const IcoSignal = () => (
  <svg width="16" height="14" viewBox="0 0 26 20" fill="none" stroke="#595959" strokeWidth="1.9" strokeLinecap="round">
    {/* Center dot */}
    <circle cx="13" cy="14" r="1.6" fill="#595959" stroke="none"/>
    {/* Right arcs */}
    <path d="M16.2 10.8 a4.5 4.5 0 0 1 0 6.4"/>
    <path d="M19.5 7.5  a9   9   0 0 1 0 13"/>
    {/* Left arcs */}
    <path d="M9.8  10.8 a4.5 4.5 0 0 0 0 6.4"/>
    <path d="M6.5  7.5  a9   9   0 0 0 0 13"/>
  </svg>
)

const IcoUnauth = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
    <circle cx="10" cy="7" r="4" stroke="currentColor" strokeWidth="2"/>
    <path d="M3 20c0-3.9 3.1-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <line x1="16" y1="13" x2="21" y2="18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
    <line x1="21" y1="13" x2="16" y2="18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
  </svg>
)

const IcoZone = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="18" height="18" rx="2" stroke="white" strokeWidth="1.9" strokeDasharray="3.5 2.2"/>
    <line x1="7" y1="7" x2="17" y2="17" stroke="white" strokeWidth="2" strokeLinecap="round"/>
    <line x1="17" y1="7" x2="7" y2="17" stroke="white" strokeWidth="2" strokeLinecap="round"/>
  </svg>
)

// ── Pure helpers ──────────────────────────────────────────────────────────────

function getTowingStateFromEvents(towEvents, currentTs) {
  if (!towEvents || !towEvents.length) return {}
  const byBeacon = {}
  for (const ev of towEvents) {
    if (ev.synthetic) continue
    const ts = Number(ev.eventTs)
    if (ts > currentTs) continue
    if (!byBeacon[ev.instanceId] || ts > Number(byBeacon[ev.instanceId].eventTs)) {
      byBeacon[ev.instanceId] = ev
    }
  }
  return byBeacon
}

function getNameInitials(name) {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  return ((parts[0] || '')[0] || '') + ((parts[1] || '')[0] || '')
}

function latestRssiForMac(data, mac, currentTs) {
  if (!data || !data.length) return null
  let best = null
  for (const r of data) {
    const m = r.instanceId || r.instanceid || r.instancsid || r.mac || ''
    if (m !== mac) continue
    const ts = Number(r.timestamp)
    if (ts > currentTs) continue
    if (!best || ts > Number(best.timestamp)) best = r
  }
  if (!best) return null
  const v = best.rssi || best.RSSI || best.rssiValue
  return v != null ? Number(v) : null
}

// RSSI range -120 dBm (0%) → -20 dBm (100%)
function rssiToPercent(rssi) {
  if (rssi == null) return 55
  return Math.max(4, Math.min(100, ((rssi + 120) / 100) * 100))
}

function rssiToColor(rssi) {
  if (rssi == null) return '#52c41a'
  if (rssi >= -60) return '#52c41a'
  if (rssi >= -75) return '#fa8c16'
  return '#f5222d'
}

// Mirrors Map.js isValidScannedId — valid non-zero hex string of even length
function isValidScannedId(id) {
  const v = `${id || ''}`.trim().replace(/^0x/i, '')
  return v.length > 0 && v.length % 2 === 0 && /^[0-9a-fA-F]+$/.test(v) && !/^0+$/.test(v)
}

const STATUS = {
  MOVING:  { color: '#52c41a' },
  IDLE:    { color: '#fa8c16' },
  STOP:    { color: '#8c8c8c' },
  WARNING: { color: '#fa8c16' },
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function MapTooltipContent({
  currentData,
  index,
  filteredFleetRangeData,
  towingData,
  manpowerForPoint,
  bleAssetsMap,
  asset,
  isInvalid,
  overspeedEvents = [],
  unauthorizedZoneEvents = [],
}) {
  const currentTs = Number(currentData.timestamp)

  // Motion state — mirrors DeviceInfoData logic
  const mv = !!(
    (currentData.movement    && Number(currentData.movement)) ||
    (currentData.eye_bea_mov_1 && Number(currentData.eye_bea_mov_1) === 1)
  )
  const ign = !!(
    (currentData.dig_ign  && Number(currentData.dig_ign)) ||
    (currentData.ignition && Number(currentData.ignition))
  )

  // Speed & overspeed — driven by overspeed_summary events (start_timestamp → end_timestamp windows)
  const speed = currentData.speed_oem || currentData.speed_gnss
  const activeOverspeedEvent = (overspeedEvents || []).find(ev => {
    const start = Number(ev.start_timestamp || ev.start_time || ev.startTs || 0)
    const end   = Number(ev.end_timestamp   || ev.end_time   || ev.endTs   || 0)
    return start && end && currentTs >= start && currentTs <= end
  }) || null
  const isOverSpeed = activeOverspeedEvent != null
  const evSpeedLimit = activeOverspeedEvent
    ? (activeOverspeedEvent.speed_limit != null ? Number(activeOverspeedEvent.speed_limit) : (asset && asset.speedLimit != null ? Number(asset.speedLimit) : null))
    : null
  const evAvgSpeed = activeOverspeedEvent
    ? (activeOverspeedEvent.avg_speed != null ? activeOverspeedEvent.avg_speed : (activeOverspeedEvent.average_speed != null ? activeOverspeedEvent.average_speed : null))
    : null
  const evMaxSpeed = activeOverspeedEvent && activeOverspeedEvent.max_speed != null ? activeOverspeedEvent.max_speed : null

  // prevTs needed for both disconnected alert window and LOST beacon filtering
  const prevTs = index > 0
    ? Number((filteredFleetRangeData[index - 1] || {}).timestamp || currentTs)
    : currentTs

  // Towing state at current frame
  const allTowingState = (towingData && towingData.towEvents)
    ? getTowingStateFromEvents(towingData.towEvents, currentTs)
    : {}
  const activeTowingBeacons = Object.values(allTowingState).filter(ev => ev.status === 'TOWING')
  // Only show disconnect alert on the exact frame the event fires, not for all subsequent frames
  const disconnectedBeacons = Object.values(allTowingState).filter(ev =>
    ev.status === 'DISCONNECTED' &&
    Number(ev.eventTs) > prevTs &&
    Number(ev.eventTs) <= currentTs
  )

  // Detected BLE (towing MACs excluded)
  const towingMacs = new Set(activeTowingBeacons.map(ev => ev.instanceId))

  const activeNearbyBle = (() => {
    if (!towingData || !towingData.data || !towingData.data.length) return []
    const byMac = {}
    for (const r of towingData.data) {
      const mac = r.instanceId || r.instanceid || r.instancsid || r.mac || ''
      if (!mac) continue
      const ts = Number(r.timestamp)
      if (ts > currentTs) continue
      if (!byMac[mac] || ts > byMac[mac].ts) byMac[mac] = { r, ts }
    }
    return Object.values(byMac)
      .filter(({ r, ts }) => {
        const mac = r.instanceId || r.instanceid || r.instancsid || r.mac || ''
        if (towingMacs.has(mac)) return false
        const status = (r.status || '').toUpperCase()
        if (status !== 'LOST') return true
        return ts > prevTs
      })
      .map(({ r }) => r)
  })()

  // Awaiting Authorization: dig_ign=1, dout=0, scanned_id present
  const showAwaitingAuth = (
    Number(currentData.dig_ign) === 1 &&
    Number(currentData.dout)    === 0 &&
    currentData.scanned_id != null &&
    currentData.scanned_id !== ''
  )

  // Unauthorised Operator: dig_ign=1, dout=1, but scanned_id invalid or no manpower match
  const showUnauthorisedOperator = (
    Number(currentData.dig_ign) === 1 &&
    Number(currentData.dout)    === 1 &&
    (!isValidScannedId(currentData.scanned_id) || !manpowerForPoint)
  )

  // Restricted zone: active when currentTs falls within an unauthorized_zone_event window
  const restrictedZoneActive = (unauthorizedZoneEvents || []).some(ev => {
    const start = Number(ev.start_timestamp || 0)
    const end   = Number(ev.end_timestamp   || 0)
    return start && end && currentTs >= start && currentTs <= end
  })

  // Display status
  const hasWarning = disconnectedBeacons.length > 0 || restrictedZoneActive || showUnauthorisedOperator
  const motionStatus = (ign && mv) ? 'MOVING' : ign ? 'IDLE' : 'STOP'
  const { color: statusColor } = STATUS[motionStatus]

  // Section flags
  const hasAlerts   = isOverSpeed || disconnectedBeacons.length > 0 || restrictedZoneActive
  const hasTowing   = activeTowingBeacons.length > 0
  const hasDetected = activeNearbyBle.length > 0
  const hasManpower = !!(manpowerForPoint && manpowerForPoint.name)

  // Asset label: "assetCode / ownerCode" or fallback to mac
  const getAsset = (mac) => bleAssetsMap && bleAssetsMap[mac]
  const getLabel = (mac) => {
    const a = getAsset(mac)
    if (!a) return mac
    const parts = [a.assetCode, a.ownerCode].filter(Boolean)
    return parts.length ? parts.join(' / ') : mac
  }

  const sep = { borderBottom: '1px solid rgba(0,0,0,0.07)', paddingBottom: 10, marginBottom: 10 }

  return (
    <div style={{ minWidth: 250, fontFamily: 'inherit', fontSize: 12, paddingBottom: 6 }}>

      {/* ── 1. Header ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', ...sep }}>

        <div>
          <div style={{ fontSize: 9, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
            Last Update
          </div>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#262626' }}>
            {moment(currentData.timestamp).format('HH:mm:ss')}
          </div>
          {isInvalid && (
            <div style={{ fontSize: 9, color: '#f5222d', marginTop: 2 }}>Invalid Location</div>
          )}
        </div>

        <div style={{ textAlign: 'right' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, marginBottom: 3 }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: statusColor, letterSpacing: '0.05em' }}>{motionStatus}</span>
          </div>
          {speed != null && speed !== '' && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, justifyContent: 'flex-end' }}>
              <span style={{ fontSize: 20, fontWeight: 400, lineHeight: 1, color: '#1890ff' }}>{speed}</span>
              <span style={{ fontSize: 10, color: '#8c8c8c' }}>km/h</span>
            </div>
          )}
        </div>
      </div>

      {/* ── 2. Alerts ─────────────────────────────────────────────────────── */}
      {hasAlerts && (
        <div style={sep}>

          {isOverSpeed && (
            <div style={{ background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6, padding: '8px 10px', marginBottom: disconnectedBeacons.length ? 6 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ background: '#f5222d', borderRadius: 5, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <IcoWarning />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 10, color: '#f5222d', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                    Overspeeding Alert
                  </div>
                  <div style={{ display: 'flex', gap: 14 }}>
                    {evSpeedLimit != null && (
                      <div>
                        <div style={{ fontSize: 8, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 1 }}>Limit</div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#262626', lineHeight: 1 }}>{evSpeedLimit} <span style={{ fontSize: 9 }}>km/h</span></div>
                      </div>
                    )}
                    {evAvgSpeed != null && (
                      <div>
                        <div style={{ fontSize: 8, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 1 }}>Avg</div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#fa8c16', lineHeight: 1 }}>{Math.round(evAvgSpeed)} <span style={{ fontSize: 9 }}>km/h</span></div>
                      </div>
                    )}
                    {evMaxSpeed != null && (
                      <div>
                        <div style={{ fontSize: 8, color: '#8c8c8c', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 1 }}>Max</div>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#f5222d', lineHeight: 1 }}>{Math.round(evMaxSpeed)} <span style={{ fontSize: 9 }}>km/h</span></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {disconnectedBeacons.map((ev, i) => {
            const label = getLabel(ev.instanceId)
            return (
              <div key={ev.instanceId} style={{ background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6, padding: '8px 10px', marginTop: (isOverSpeed || i > 0) ? 6 : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ background: '#f5222d', borderRadius: 5, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <IcoDisconnect />
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 10, color: '#f5222d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Towing Asset Disconnected
                    </div>
                    <div style={{ fontWeight: 600, fontSize: 11, color: '#595959', marginTop: 2 }}>{label}</div>
                  </div>
                </div>
              </div>
            )
          })}

          {restrictedZoneActive && (
            <div style={{ background: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6, padding: '8px 10px', marginTop: (isOverSpeed || disconnectedBeacons.length > 0) ? 6 : 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ background: '#f5222d', borderRadius: 5, width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <IcoZone />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 10, color: '#f5222d', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Restricted Zone Entry
                  </div>
                  <div style={{ fontSize: 10, color: '#595959', marginTop: 2 }}>Vehicle entered a restricted area</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── 3. Awaiting Authorization ─────────────────────────────────────── */}
      {showAwaitingAuth && (
        <div style={{ textAlign: 'center', ...sep }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: 'rgba(250,173,20,0.10)', border: '1px solid rgba(250,173,20,0.45)',
            borderRadius: 20, padding: '4px 14px',
            fontSize: 10, fontWeight: 700, color: '#ad6800', letterSpacing: '0.04em',
          }}>
            <IcoClock /> Awaiting Authorization
          </span>
        </div>
      )}

      {showUnauthorisedOperator && (
        <div style={{ textAlign: 'center', ...sep }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5,
            background: 'rgba(245,34,45,0.08)', border: '1px solid rgba(245,34,45,0.40)',
            borderRadius: 20, padding: '4px 14px',
            fontSize: 10, fontWeight: 700, color: '#cf1322', letterSpacing: '0.04em',
          }}>
            <IcoUnauth /> Unauthorised Operator
          </span>
        </div>
      )}

      {/* ── 4. Operator ───────────────────────────────────────────────────── */}
      {hasManpower && (
        <div style={{ ...(hasTowing || hasDetected ? sep : {}) }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 8, background: '#1890ff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, color: '#fff', fontSize: 13, fontWeight: 800,
            }}>
              {getNameInitials(manpowerForPoint.name)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 12, color: '#262626', marginBottom: 3 }}>
                {manpowerForPoint.name}
              </div>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                background: '#f6ffed', border: '1px solid #b7eb8f',
                borderRadius: 4, padding: '1px 6px',
                fontSize: 9, fontWeight: 700, color: '#389e0d', letterSpacing: '0.03em',
                marginBottom: 3,
              }}>
                ✓ Authorized
              </span>
              {manpowerForPoint.mobileNo && (
                <div style={{ fontSize: 10, color: '#595959', marginTop: 3 }}>
                  📞 {manpowerForPoint.mobileNo}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── 5. Towing Assets ──────────────────────────────────────────────── */}
      {hasTowing && (
        <div style={{ ...(hasDetected ? sep : {}) }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <IcoTruck />
              <span style={{ fontWeight: 600, fontSize: 12, color: '#262626' }}>
                Towing Assets
              </span>
            </div>
            <span style={{
              background: '#1890ff', color: '#fff',
              fontSize: 9, fontWeight: 700, borderRadius: 2, padding: '2px 8px', letterSpacing: '0.04em',
            }}>
              {activeTowingBeacons.length} ACTIVE
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
            {activeTowingBeacons.map(ev => {
              const label    = getLabel(ev.instanceId)
              const rssi     = latestRssiForMac(towingData && towingData.data, ev.instanceId, currentTs)
              const barPct   = rssiToPercent(rssi)
              const barColor = rssiToColor(rssi)
              return (
                <div key={ev.instanceId} style={{
                  border: '1px solid #d9d9d9', borderRadius: 2,
                  padding: '7px 8px', background: '#f5f5f5',
                }}>
                  <div style={{ fontWeight: 500, fontSize: 9, color: '#262626', lineHeight: 1.3, marginBottom: 5 }}>
                    {label}
                  </div>
                  <div style={{ height: 4, background: '#e8e8e8', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${barPct}%`, background: barColor, borderRadius: 2, transition: 'width 0.4s ease' }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* ── 6. Detected Beacons ───────────────────────────────────────────── */}
      {hasDetected && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <IcoSignal />
              <span style={{ fontWeight: 600, fontSize: 12, color: '#262626' }}>
                Detected Beacons
              </span>
            </div>
            <span style={{
              background: '#52c41a', color: '#fff',
              fontSize: 9, fontWeight: 700, borderRadius: 2, padding: '2px 8px', letterSpacing: '0.04em',
            }}>
              {activeNearbyBle.length} ACTIVE
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
            {activeNearbyBle.map(r => {
              const mac      = r.instanceId || r.instanceid || r.instancsid || r.mac || '?'
              const label    = getLabel(mac)
              const rssi     = latestRssiForMac(towingData && towingData.data, mac, currentTs)
              const barPct   = rssiToPercent(rssi)
              const barColor = rssiToColor(rssi)
              return (
                <div key={mac} style={{
                  border: '1px solid #d9d9d9', borderRadius: 2,
                  padding: '7px 8px', background: '#f5f5f5',
                }}>
                  <div style={{ fontWeight: 400, fontSize: 9, color: '#434343', lineHeight: 1.3, marginBottom: 5 }}>
                    {label}
                  </div>
                  <div style={{ height: 4, background: '#e8e8e8', borderRadius: 3 }}>
                    <div style={{ height: '100%', width: `${barPct}%`, background: barColor, borderRadius: 2, transition: 'width 0.4s ease' }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
